/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { CompletedEnhanceSpeechTrack } from "components/EnhancedSpeech/Utilities";
import PlayPauseButton from "components/PlayPauseButton";
import styled from "styled-components";
import EnhancedSwitch from "components/shared/EnhancedSwitch";
import useBasicAudioPlayer from "hooks/useBasicAudioPlayer";
import { devices } from "components/shared/Styles";
import "stylesheets/spectrum.scss";

const PreviewPlaybackView = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 16px;
`;

const PreviewSwitchContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 56px;
  width: 252px;
  padding: 16px;
  border-radius: 16px;
  align-items: center;
  justify-content: center;
  background-color: rgba(
    var(--spectrum-global-color-static-indigo-600-rgb),
    0.1
  );
  margin-top: 16px;
`;

const PreviewLabel = styled.div.attrs({
  className: "spectrum-Switch-label",
})`
  text-align: start;
  font-size: 16px;
  line-height: 20px;
  font-weight: normal;
  color: var(--spectrum-global-color-static-gray-800);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  max-width: 300px;
  margin-top: 0;

  ${devices.small} {
    max-width: 250px;
  }
`;

const PreviewTrackName = styled(PreviewLabel)`
  margin-left: 18px;
  font-weight: bold;
`;

export const BasicEnhancedTrackPlayer: React.FC<{
  track: CompletedEnhanceSpeechTrack;
  onLoadingSuccess?: () => void;
  onLoadingFailed?: (error: MediaError | null) => void;
}> = ({ track, onLoadingSuccess, onLoadingFailed }) => {
  const {
    enhanced,
    isLoaded,
    isPlaying,
    togglePlayback,
    toggleEnhance,
    displayName,
  } = useBasicAudioPlayer({
    track,
    onLoadingSuccess,
    onLoadingFailed,
  });

  return (
    <div data-testid="enhance-speech-preview">
      <PreviewPlaybackView>
        <PlayPauseButton
          isPlaying={isPlaying}
          trackDescription={displayName}
          buttonDiameterPx={52}
          onClick={togglePlayback}
          isLoaded={isLoaded}
        />
        <PreviewTrackName>{displayName}</PreviewTrackName>
      </PreviewPlaybackView>
      <PreviewSwitchContainer>
        <EnhancedSwitch
          checked={enhanced ? true : undefined}
          onClick={toggleEnhance}
        />
      </PreviewSwitchContainer>
    </div>
  );
};
